import debounce from 'lodash.debounce';
import { useCallback, useEffect, useRef, useState } from 'react';
import Select from 'react-select';
import { DropdownIndicator } from './DropdownIndicator';
import { MenuList } from './MenuList';
import { Option } from './Option';
import styles from './styles.module.scss';
import { ApiResponse, OptionType } from './type';

type PaginatedSelectProps = {
  searchAction: (input: string, page: number) => Promise<ApiResponse>;
  placeholder?: string;
  isClearable?: boolean;
  onChange?: (value: OptionType | null) => void;
  onBlur?: () => void;
  value?: OptionType | null;
  defaultValue?: OptionType;
};

export const PaginatedSelect = ({
  searchAction,
  value,
  defaultValue,
  placeholder = '選択してください',
  isClearable = true,
  onChange,
  onBlur,
}: PaginatedSelectProps) => {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<OptionType[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [menuScrollTop, setMenuScrollTop] = useState(0);

  const loadOptions = useCallback(
    async (input: string, pageNum: number) => {
      const res = await searchAction(input, pageNum);
      const total = Number(res.headers['total-pages'] || '1');
      return { options: res.options, totalPages: total };
    },
    [searchAction]
  );

  // loadOptions の最新参照を ref で保持し、debouncedSearch を安定化して debounce が毎レンダーでリセットされるのを防ぐ
  const loadOptionsRef = useRef(loadOptions);
  loadOptionsRef.current = loadOptions;

  const debouncedSearch = useCallback(
    debounce(async (input: string) => {
      const { options: newOptions, totalPages } = await loadOptionsRef.current(input, 1);
      setOptions(newOptions);
      setPage(1);
      setTotalPages(totalPages);
    }, 300),
    []
  );

  const loadMore = async (scrollTop: number) => {
    setMenuScrollTop(scrollTop);
    const { options: moreOptions } = await loadOptions(inputValue, page + 1);
    setOptions(prev => [...prev, ...moreOptions]);
    setPage(prev => prev + 1);
  };

  useEffect(() => {
    debouncedSearch('');
    // コンポーネント unmount 時に pending な debounce タイマーをキャンセルする。
    // キャンセルしないと unmount 後に searchAction が発火し、
    // テスト環境では MSW ハンドラ解除後のリクエストが Unhandled Rejection になる。
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  return (
    <Select
      className={styles.select}
      classNamePrefix='single-select'
      menuIsOpen={menuIsOpen}
      onMenuOpen={() => setMenuIsOpen(true)}
      onMenuClose={() => setMenuIsOpen(false)}
      value={value !== undefined ? value : defaultValue}
      components={{
        IndicatorSeparator: () => null,
        ClearIndicator: () => null,
        Option,
        DropdownIndicator,
        MenuList: props => (
          <MenuList
            {...props}
            page={page}
            totalPages={totalPages}
            loadMore={loadMore}
            menuScrollTop={menuScrollTop}
          />
        ),
      }}
      options={options}
      onInputChange={val => {
        setInputValue(val);
        setMenuScrollTop(0);
        setTotalPages(1);
        debouncedSearch(val);
        return val;
      }}
      onChange={onChange}
      onBlur={onBlur}
      placeholder={placeholder}
      isClearable={isClearable}
      noOptionsMessage={() => '選択項目がありません'}
      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
      menuPosition='fixed'
      styles={{ menuPortal: base => ({ ...base, zIndex: 99999 }) }}
    />
  );
};
